import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import {
  Box,
  CircularProgress,
  CssBaseline,
  Fab,
  Grid,
  TextField as InputField,
  Paper,
  Theme,
  Typography
} from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-mui";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { CREATE_PROTECTION_PLAN } from "../../../../../graphql/protectionPlans/createProtectionPlanMutation";
import { GET_PROTECTION_PLAN } from "../../../../../graphql/protectionPlans/getProtectionPlanQuery";
import { UPDATE_PROTECTION_PLAN } from "../../../../../graphql/protectionPlans/updateProctionPlanMutation";
import { protectionPlanInitialState } from "../../../../../reducers/protectionPlan/consts";
import { IProtectionPlan, IProtectionPlanInput } from "../../../../../reducers/protectionPlan/types";
import { IAppState } from "../../../../../store";
import { FloatInput } from "../../../../common/FloatInput/FloatInput";
import { useSnackBar } from "../../../../common/SnackBarContext/SnackBarContext";
import { SnackBarVariant } from "../../../../common/SnackbarWrapper/SnackbarWrapper";
import {
  formatGraphQLErrorMessage,
  toCurrency
} from "../../../../common/utils";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      backgroundColor: "#f4f4fa",
      borderRadius: "10px",
      display: "flex",
      flexDirection: "row",
      height: "100%"
    },
    content: {
      height: "auto",
      marginLeft: "50px",
      marginRight: "50px",
      marginTop: "100px"
    },
    root: {
      padding: theme.spacing(3),
      flexGrow: 1
    },
    iconStyle: {
      padding: 2
    },
    avatarStyle: {
      background: "var(--theme-accent)",
      height: 25,
      width: 25
    },
    ruleCardMain: {
      position: "relative"
    },
    section: {
      marginBottom: "25px"
    }
  })
);

const ProtectionPlan: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const snackbar = useSnackBar();
  const navigate = useNavigate();

  const userState = useSelector((state: IAppState) => state.userReducer);

  const { locale, currency } = userState.currentOrganisation;

  const [isNewProtectionPlan, setIsNewProtectionPlan] = useState<boolean>(true);
  const [values, setValues] = useState<IProtectionPlan>(
    _.cloneDeep(protectionPlanInitialState)
  );

  const [
    loadProtectionPlan,
    { loading: loadingProtectionPlan, data }
  ] = useLazyQuery(GET_PROTECTION_PLAN, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (!data.protectionPlan) {
        navigate("/protection-plans");
      }
      setValues(data.protectionPlan);
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  const [
    createProtectionPlan,
    { loading: createProtectionPlanLoading }
  ] = useMutation(CREATE_PROTECTION_PLAN, {
    onCompleted: () => {
      snackbar({
        message: formatGraphQLErrorMessage(
          "Protection plan created successfully."
        ),
        variant: SnackBarVariant.SUCCESS
      });
      navigate("/protection-plans");
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
      navigate("/protection-plans");
    }
  });

  const [
    updateProtectionPlan,
    { loading: updateProtectionPlanLoading }
  ] = useMutation(UPDATE_PROTECTION_PLAN, {
    onCompleted: () => {
      snackbar({
        message: formatGraphQLErrorMessage(
          "Protection plan updated successfully."
        ),
        variant: SnackBarVariant.SUCCESS
      });
      navigate("/protection-plans");
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
      navigate("/protection-plans");
    }
  });

  useEffect(() => {
    if (location && location.search) {
      const params = new URLSearchParams(location.search);
      const protectionPlanId = params.get("protectionPlanId");
      if (protectionPlanId) {
        setIsNewProtectionPlan(false);
        loadProtectionPlan({
          variables: {
            id: protectionPlanId
          }
        });
      }
    }
  }, [location]);

  const _createProtectionPlan = (protectionPlanInput: IProtectionPlanInput) => {
    createProtectionPlan({
      variables: {
        protectionPlan: protectionPlanInput
      }
    });
  };

  const _updateProtectionPlan = (protectionPlanInput: IProtectionPlan) => {
    updateProtectionPlan({
      variables: {
        protectionPlan: protectionPlanInput
      }
    });
  };

  const handleFormSubmit = () => {
    const { id, ...rest } = values;
    if (!id) {
      _createProtectionPlan(rest);
    } else {
      _updateProtectionPlan(values);
    }
  };

  const protectionPlanSchema = Yup.object().shape({
    name: Yup.string().required("Name is required."),
    description: Yup.string()
      .min(5)
      .required("Description is required."),
    excess: Yup.number().required("Excess is required")
  });

  if (loadingProtectionPlan) {
    return <CircularProgress />;
  }

  return (
    <Grid container spacing={2}>
      <CssBaseline />
      <Grid container item xs={6} alignItems="center">
        <Typography variant="h1" color="primary">
          Protection Plan
        </Typography>
        <Box color="white" sx={{ pr: 1 }}></Box>
        <DoubleArrowIcon />
        <Box color="white" sx={{ pl: 1 }}></Box>
        {!isNewProtectionPlan ? (
          <Typography variant="h1" color="primary">
            Update Plan
          </Typography>
        ) : (
          <Typography variant="h1" color="primary">
            New Plan
          </Typography>
        )}
      </Grid>
      <Grid container item xs={12}>
        <Paper className={classes.root}>
          <Formik
            enableReinitialize
            validationSchema={protectionPlanSchema}
            initialValues={values}
            onSubmit={(values, { setSubmitting }) => {
              handleFormSubmit();
              setSubmitting(false);
            }}
          >
            {(props) => (
              <Form>
                <Grid container className={classes.section} spacing={2}>
                  <Grid item container xs={12} spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Field
                        component={TextField}
                        placeholder="Name"
                        label="Name"
                        name={"displayName"}
                        InputProps={{
                          onChange: (
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setValues({
                              ...props.values,
                              name: event.target.value
                            });
                          },
                          value: props.values.name
                        }}
                        inputProps={{ maxLength: 35 }}
                        fullWidth
                        required
                      ></Field>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Field
                        component={TextField}
                        placeholder="Code"
                        label="Code"
                        name={"code"}
                        InputProps={{
                          onChange: (
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setValues({
                              ...props.values,
                              code: event.target.value
                            });
                          },
                          value: props.values.code
                        }}
                        inputProps={{ maxLength: 35 }}
                        fullWidth
                      ></Field>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Field
                        component={TextField}
                        label="Excess"
                        name={"excess"}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          onChange: (
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            const value = event.target.value
                              ? parseFloat(event.target.value)
                              : 0;
                            setValues({
                              ...props.values,
                              excess: isNaN(value) ? 0 : value
                            });
                          },
                          value: props.values.excess
                            ? props.values.excess.toString()
                            : "",
                          inputComponent: FloatInput as any
                        }}
                        inputProps={{
                          hasCurrencyPrefix: true,
                          allowNegative: false
                        }}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                  <Grid item container xs={6} sm={12}>
                    <Field
                      component={TextField}
                      placeholder="Description"
                      label="Description"
                      name={"description"}
                      multiline
                      required
                      rows={3}
                      fullWidth
                      InputProps={{
                        onChange: (
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          setValues({
                            ...props.values,
                            description: event.target.value
                          });
                        },
                        value: props.values.description
                          ? props.values.description
                          : ""
                      }}
                    ></Field>
                  </Grid>
                  <Grid item xs={6}></Grid>

                  <Grid className={classes.section} container item xs={12}>
                    <Fab
                      variant="extended"
                      size="medium"
                      aria-label="add"
                      type="submit"
                      disabled={
                        createProtectionPlanLoading ||
                        updateProtectionPlanLoading
                      }
                    >
                      {(createProtectionPlanLoading ||
                        updateProtectionPlanLoading) && (
                          <CircularProgress
                            size={14}
                            style={{ color: "white", marginRight: "10px" }}
                          />
                        )}
                      Save
                    </Fab>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ProtectionPlan;
